import React from 'react';
import { User, MessageSquare } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import type { Conversation } from '../../services/messagingService';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
  isLoading?: boolean;
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  isLoading,
}: ConversationListProps) {
  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Maintenant';
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Badge className="bg-blue-100 text-blue-800 text-[10px] px-1.5">Admin</Badge>;
      case 'PROFESSIONAL':
        return <Badge className="bg-purple-100 text-purple-800 text-[10px] px-1.5">Pro</Badge>;
      case 'CLIENT':
        return <Badge className="bg-green-100 text-green-800 text-[10px] px-1.5">Client</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-100">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-32" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <MessageSquare className="h-12 w-12 text-gray-300 mb-4" />
        <p className="text-gray-500 text-sm">Aucune conversation</p>
        <p className="text-gray-400 text-xs mt-1">
          Vos messages apparaîtront ici
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {conversations.map((conversation) => {
        const { otherParticipant, lastMessage, unreadCount, lastMessageAt } = conversation;
        const isSelected = selectedId === conversation.id;

        return (
          <button
            key={conversation.id}
            onClick={() => onSelect(conversation)}
            className={cn(
              'flex items-center gap-3 p-3 text-left transition-colors border-b border-gray-100',
              isSelected
                ? 'bg-orange-50 border-l-2 border-l-[#FF9F68]'
                : 'hover:bg-gray-50'
            )}
          >
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={cn(
                    'font-medium text-sm truncate',
                    unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'
                  )}>
                    {otherParticipant.firstName} {otherParticipant.lastName}
                  </span>
                  {getRoleBadge(otherParticipant.role)}
                  {unreadCount > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0">
                  {formatTime(lastMessageAt)}
                </span>
              </div>
              {lastMessage && (
                <p className={cn(
                  'text-xs truncate',
                  unreadCount > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'
                )}>
                  {lastMessage.content}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default ConversationList;

